// Purpose: /admin/approvals — admin-only PENDING dropbox approvals table
// with Phase 7 bulk operations. Per-row approve/deny still inline; bulk
// approve + bulk deny modals operate on the checkbox selection.
//
// Phase 7.5 port: moved from pages/AdminApprovals.tsx; PageHeader,
// DataTable, Card bulk-toolbar, Button/Modal/Select/Input primitives.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  approveApproval,
  bulkApproveApprovals,
  bulkDenyApprovals,
  denyApproval,
  listPendingApprovals,
  type ApprovalEntry,
} from '../../api/admin-approvals.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { ArrowLeftIcon } from '../../components/icons/index.tsx';
import { PageHeader } from '../../components/nav/PageHeader.tsx';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Input,
  Modal,
  Select,
  type Column,
} from '../../components/ui/index.ts';

const EXPIRY_OPTIONS = [
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '1 year', days: 365 },
];

export function AdminApprovals() {
  const { status, user } = useAuth();
  const [approvals, setApprovals] = useState<ApprovalEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [expiryDays, setExpiryDays] = useState<Record<string, number>>({});
  const [denyReasons, setDenyReasons] = useState<Record<string, string>>({});
  const [issuedLinks, setIssuedLinks] = useState<
    Record<string, { url: string; expiresAt: string }>
  >({});

  // Phase 7 bulk-select state.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkModal, setBulkModal] = useState<'approve' | 'deny' | null>(null);
  const [bulkExpiryDays, setBulkExpiryDays] = useState(90);
  const [bulkDenyReason, setBulkDenyReason] = useState('');
  const [bulkWorking, setBulkWorking] = useState(false);

  async function reload() {
    try {
      const fresh = await listPendingApprovals();
      setApprovals(fresh);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  useEffect(() => {
    if (status !== 'authenticated') return;
    void reload();
  }, [status]);

  if (status === 'loading') return <p className="text-body-m text-fg-muted">Loading...</p>;
  if (status === 'unauthenticated' || user?.role !== 'ADMIN') {
    return <p className="text-body-m text-status-danger">Admin access required.</p>;
  }

  async function handleApprove(approval: ApprovalEntry): Promise<void> {
    setActingOn(approval.id);
    setError(null);
    try {
      const days = expiryDays[approval.id] ?? 90;
      const expiresAt = new Date(Date.now() + days * 24 * 3600 * 1000).toISOString();
      const res = await approveApproval(approval.id, { expiresAt });
      setIssuedLinks((prev) => ({
        ...prev,
        [approval.id]: { url: res.linkUrl, expiresAt: res.expiresAt },
      }));
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActingOn(null);
    }
  }

  async function handleDeny(approval: ApprovalEntry): Promise<void> {
    const reason = denyReasons[approval.id]?.trim() ?? '';
    if (reason.length < 3) {
      setError('Denial reason must be at least 3 characters.');
      return;
    }
    setActingOn(approval.id);
    setError(null);
    try {
      await denyApproval(approval.id, reason);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActingOn(null);
    }
  }

  const selectableIds =
    approvals?.filter((a) => a.requester?.id !== user?.id).map((a) => a.id) ?? [];
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggleOne = (id: string, checked: boolean): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean): void => {
    setSelected(checked ? new Set(selectableIds) : new Set());
  };

  const runBulkApprove = async (): Promise<void> => {
    setBulkWorking(true);
    setError(null);
    try {
      const expiresAt = new Date(Date.now() + bulkExpiryDays * 24 * 3600 * 1000).toISOString();
      await bulkApproveApprovals({
        approvalIds: Array.from(selected),
        expiresAt,
      });
      setBulkModal(null);
      setSelected(new Set());
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBulkWorking(false);
    }
  };

  const runBulkDeny = async (): Promise<void> => {
    if (bulkDenyReason.trim().length < 3) {
      setError('Denial reason must be at least 3 characters.');
      return;
    }
    setBulkWorking(true);
    setError(null);
    try {
      await bulkDenyApprovals({
        approvalIds: Array.from(selected),
        reason: bulkDenyReason.trim(),
      });
      setBulkModal(null);
      setBulkDenyReason('');
      setSelected(new Set());
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBulkWorking(false);
    }
  };

  const columns: Column<ApprovalEntry>[] = [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={(e) => toggleAll(e.target.checked)}
          aria-label="Select all"
        />
      ),
      width: '40px',
      cell: (a) => {
        const isSelf = a.requester?.id === user?.id;
        return isSelf ? null : (
          <input
            type="checkbox"
            checked={selected.has(a.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => toggleOne(a.id, e.target.checked)}
            aria-label={`Select ${a.requester?.displayName ?? a.id}`}
          />
        );
      },
    },
    {
      key: 'requester',
      header: 'Requester',
      cell: (a) => (
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-body-m text-fg-primary truncate">
            {a.requester?.displayName ?? '(unknown)'}
          </span>
          <span className="text-body-s text-fg-muted truncate">{a.requester?.email}</span>
        </div>
      ),
    },
    {
      key: 'label',
      header: 'Label',
      hideOnMobile: true,
      cell: (a) => (
        <span className="text-body-s text-fg-primary">{a.dropbox?.label ?? '(no label)'}</span>
      ),
    },
    {
      key: 'justification',
      header: 'Justification',
      hideOnMobile: true,
      cell: (a) => (
        <div className="max-w-md whitespace-pre-wrap text-body-s text-fg-secondary">
          {a.justification}
        </div>
      ),
    },
    {
      key: 'requested',
      header: 'Requested',
      hideOnMobile: true,
      cell: (a) => (
        <span className="text-body-s text-fg-muted">
          {new Date(a.requestedAt).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      width: '320px',
      cell: (a) => {
        const issued = issuedLinks[a.id];
        const isSelf = a.requester?.id === user?.id;
        if (issued) {
          return (
            <div className="flex flex-col gap-1 text-body-s text-status-success">
              <span>
                Approved.{' '}
                <button
                  type="button"
                  onClick={() => void navigator.clipboard.writeText(issued.url)}
                  className="underline"
                >
                  Copy link
                </button>{' '}
                (expires {new Date(issued.expiresAt).toLocaleString()})
              </span>
            </div>
          );
        }
        if (isSelf) {
          return <Badge tone="warning">Self-request — another admin must decide</Badge>;
        }
        return (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Select
                value={String(expiryDays[a.id] ?? 90)}
                onChange={(e) =>
                  setExpiryDays((prev) => ({ ...prev, [a.id]: Number(e.target.value) }))
                }
                aria-label="Expiry"
              >
                {EXPIRY_OPTIONS.map((opt) => (
                  <option key={opt.days} value={opt.days}>
                    {opt.label}
                  </option>
                ))}
              </Select>
              <Button
                variant="primary"
                size="sm"
                disabled={actingOn === a.id}
                onClick={() => void handleApprove(a)}
              >
                Approve
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Deny reason"
                value={denyReasons[a.id] ?? ''}
                onChange={(e) => setDenyReasons((prev) => ({ ...prev, [a.id]: e.target.value }))}
                className="flex-1"
              />
              <Button
                variant="danger"
                size="sm"
                disabled={actingOn === a.id}
                onClick={() => void handleDeny(a)}
              >
                Deny
              </Button>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Pending dropbox approvals"
        actions={
          <Link to="/dropboxes">
            <Button variant="ghost" size="md" leftIcon={<ArrowLeftIcon size={16} />}>
              All dropboxes
            </Button>
          </Link>
        }
      />

      {selected.size > 0 ? (
        <Card padding="compact" className="flex items-center justify-between gap-4">
          <span className="text-body-m text-fg-primary">{selected.size} selected</span>
          <div className="flex gap-2">
            <Button variant="primary" size="sm" onClick={() => setBulkModal('approve')}>
              Bulk approve
            </Button>
            <Button variant="danger" size="sm" onClick={() => setBulkModal('deny')}>
              Bulk deny
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        </Card>
      ) : null}

      {error ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
        >
          {error}
        </div>
      ) : null}

      {approvals === null ? <p className="text-body-m text-fg-muted">Loading...</p> : null}
      {approvals && approvals.length === 0 ? (
        <p className="text-body-m text-fg-secondary">No pending approvals.</p>
      ) : null}
      {approvals && approvals.length > 0 ? (
        <DataTable<ApprovalEntry>
          columns={columns}
          rows={approvals}
          getRowKey={(a) => a.id}
          caption="Pending dropbox approvals"
        />
      ) : null}

      <Modal
        open={bulkModal === 'approve'}
        onClose={() => setBulkModal(null)}
        title={`Bulk approve ${selected.size} request${selected.size === 1 ? '' : 's'}`}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setBulkModal(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={bulkWorking}
              onClick={() => void runBulkApprove()}
            >
              Approve all
            </Button>
          </>
        }
      >
        <p>Every selected request gets the same expiry. Atomic — any failure rolls all back.</p>
        <Select
          className="mt-3"
          label="Expiry"
          value={String(bulkExpiryDays)}
          onChange={(e) => setBulkExpiryDays(Number(e.target.value))}
        >
          {EXPIRY_OPTIONS.map((opt) => (
            <option key={opt.days} value={opt.days}>
              {opt.label}
            </option>
          ))}
        </Select>
      </Modal>

      <Modal
        open={bulkModal === 'deny'}
        onClose={() => setBulkModal(null)}
        title={`Bulk deny ${selected.size} request${selected.size === 1 ? '' : 's'}`}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setBulkModal(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={bulkWorking}
              disabled={bulkDenyReason.trim().length < 3}
              onClick={() => void runBulkDeny()}
            >
              Deny all
            </Button>
          </>
        }
      >
        <p>All selected requests are denied with the same reason.</p>
        <Input
          className="mt-3"
          label="Reason (≥3 chars)"
          value={bulkDenyReason}
          onChange={(e) => setBulkDenyReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}
